import React, { useState, useEffect } from "react";
import {
  Edit,
  TabbedForm,
  FormTab,
  TextInput,
  SelectInput,
  useRecordContext,
  useDataProvider,
  useNotify,
  useRefresh,
  Button,
  TopToolbar,
  FunctionField,
  TextField,
  BooleanField,
  CreateButton,
  EditButton,
  DeleteButton,
  useDelete,
  useCreate,
  useUpdate,
  useInput,
  ReferenceInput,
  AutocompleteInput,
  ReferenceManyField,
  Datagrid,
  ChipField,
  DateField,
} from "react-admin";
import { useNavigate } from "react-router-dom";
import { useConfirm } from "../../hooks/useConfirm";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField as MuiTextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Button as MuiButton,
  Checkbox,
  FormControlLabel,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { ApiKeyManagement } from "../../components/apikey/ApiKeyManagement";
import { TenantConfigSync } from "../../components/tenant/TenantConfigSync";

const tenantTypeMap = {
  RESTAURANT: "Restaurant",
  RETAIL: "Retail",
};

// Brand Management Component
const BrandManagementSection = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { confirm, ConfirmDialog } = useConfirm();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openBrandDialog, setOpenBrandDialog] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [brandForm, setBrandForm] = useState({ name: "", description: "", logoUrl: "" });
  const [logoFile, setLogoFile] = useState(null);
  const [deleteOne] = useDelete();
  const [create] = useCreate();
  const [update] = useUpdate();

  useEffect(() => {
    if (record?.id) {
      loadBrands();
    } else {
      // Clear brands when creating a new tenant (no ID yet)
      setBrands([]);
    }
  }, [record?.id]);

  const loadBrands = async () => {
    if (!record?.id) {
      setBrands([]);
      return;
    }
    
    setLoading(true);
    try {
      const { data } = await dataProvider.getList("brand", {
        filter: { tenantId: record.id },
        pagination: { page: 1, perPage: 100 },
        sort: { field: "name", order: "ASC" },
      });
      setBrands(data);
    } catch (error) {
      console.error("Failed to load brands:", error);
      notify("Failed to load brands", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenBrandDialog = (brand = null) => {
    if (brand) {
      setEditingBrand(brand);
      setBrandForm({ name: brand.name, description: brand.description || "", logoUrl: brand.logoUrl || "" });
    } else {
      setEditingBrand(null);
      setBrandForm({ name: "", description: "", logoUrl: "" });
    }
    setLogoFile(null);
    setOpenBrandDialog(true);
  };

  const handleCloseBrandDialog = () => {
    setOpenBrandDialog(false);
    setEditingBrand(null);
    setBrandForm({ name: "", description: "", logoUrl: "" });
    setLogoFile(null);
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogoFile(file);
      // Convert to base64 for preview and storage
      const reader = new FileReader();
      reader.onloadend = () => {
        setBrandForm({ ...brandForm, logoUrl: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveBrand = async () => {
    try {
      if (editingBrand) {
        // Update existing brand - set explicit tenantId + context
        await update("brand", {
          id: editingBrand.id,
          data: { 
            ...editingBrand, 
            ...brandForm,
            tenantId: record.id,  // Explicit foreign key
            context: { 
              ...editingBrand.context,
              tenantId: record.id 
            }
          },
          previousData: editingBrand,
        });
        notify("Brand updated successfully", { type: "success" });
      } else {
        // Create new brand
        await create("brand", {
          data: {
            ...brandForm,
            tenantId: record.id,  // Explicit foreign key
            context: { tenantId: record.id },
          },
        });
        notify("Brand created successfully", { type: "success" });
      }
      handleCloseBrandDialog();
      loadBrands();
    } catch (error) {
      console.error("Failed to save brand:", error);
      notify("Failed to save brand", { type: "error" });
    }
  };

  const handleDeleteBrand = async (brand) => {
    const confirmed = await confirm({
      title: 'Delete Brand',
      description: `Are you sure you want to delete ${brand.name}?`,
      content: (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This will permanently remove the brand and may affect associated locations.
        </Typography>
      ),
      confirmText: 'Delete',
      confirmColor: 'error',
      variant: 'warning',
    });
    
    if (!confirmed) return;
    
    try {
      await deleteOne("brand", { id: brand.id, previousData: brand });
      notify("Brand deleted successfully", { type: "success" });
      loadBrands();
    } catch (error) {
      console.error("Failed to delete brand:", error);
      notify("Failed to delete brand", { type: "error" });
    }
  };

  if (!record) return null;

  // Show message for new tenants (not yet saved)
  if (!record.id) {
    return (
      <Box>
        <Alert severity="info">
          Please save the tenant first before adding brands.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h6">Brands</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton onClick={loadBrands} size="small" title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button
            onClick={() => handleOpenBrandDialog()}
            label="Add Brand"
            variant="contained"
            startIcon={<AddIcon />}
          />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : brands.length === 0 ? (
        <Alert severity="info">
          No brands yet. Click "Add Brand" to create your first brand.
        </Alert>
      ) : (
        <Box sx={{ width: "100%" }}>
          <Grid container spacing={3} sx={{ margin: 0, width: "100%" }}>
            {brands.map((brand) => (
              <Grid item xs={12} sm={6} md={4} key={brand.id} sx={{ 
                display: "flex",
                minWidth: 300,
              }}>
                <BrandCard
                  brand={brand}
                  onEdit={() => handleOpenBrandDialog(brand)}
                  onDelete={() => handleDeleteBrand(brand)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Brand Edit Dialog */}
      <Dialog open={openBrandDialog} onClose={handleCloseBrandDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBrand ? "Edit Brand" : "Create Brand"}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <MuiTextField
              label="Name"
              value={brandForm.name}
              onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })}
              fullWidth
              margin="normal"
              required
            />
            <MuiTextField
              label="Description"
              value={brandForm.description}
              onChange={(e) => setBrandForm({ ...brandForm, description: e.target.value })}
              fullWidth
              margin="normal"
              multiline
              rows={3}
            />
            
            {/* Logo Upload */}
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Brand Logo
              </Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: 1,
                    border: "2px dashed",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "background.default",
                    overflow: "hidden",
                  }}
                >
                  {brandForm.logoUrl ? (
                    <img
                      src={brandForm.logoUrl}
                      alt="Brand logo preview"
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  ) : (
                    <BusinessIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                  )}
                </Box>
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="brand-logo-upload"
                    type="file"
                    onChange={handleLogoChange}
                  />
                  <label htmlFor="brand-logo-upload">
                    <MuiButton variant="outlined" component="span" size="small">
                      Upload Logo
                    </MuiButton>
                  </label>
                  {brandForm.logoUrl && (
                    <MuiButton
                      variant="text"
                      size="small"
                      color="error"
                      onClick={() => setBrandForm({ ...brandForm, logoUrl: "" })}
                      sx={{ ml: 1 }}
                    >
                      Remove
                    </MuiButton>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBrandDialog} label="Cancel" />
          <Button
            onClick={handleSaveBrand}
            label={editingBrand ? "Update" : "Create"}
            variant="contained"
            disabled={!brandForm.name}
          />
        </DialogActions>
      </Dialog>
      
      <ConfirmDialog />
    </Box>
  );
};

// Brand Card Component
const BrandCard = ({ brand, onEdit, onDelete }) => {
  const [locationCount, setLocationCount] = useState(0);
  const dataProvider = useDataProvider();

  useEffect(() => {
    loadLocationCount();
  }, [brand.id]);

  const loadLocationCount = async () => {
    try {
      const { total } = await dataProvider.getList("location", {
        filter: { brandId: brand.id },
        pagination: { page: 1, perPage: 1 },
        sort: { field: "name", order: "ASC" },
      });
      setLocationCount(total);
    } catch (error) {
      console.error("Failed to load location count:", error);
    }
  };

  return (
    <Card 
      variant="outlined" 
      sx={{ 
        height: 280,  // Fixed height for uniformity
        width: "100%",  // Full width of grid cell
        minWidth: 300,  // Minimum width to prevent cramping
        maxWidth: 400,  // Maximum width for readability
        display: "flex",
        flexDirection: "column",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: 3,
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent sx={{ 
        flex: 1, 
        display: "flex", 
        flexDirection: "column",
        p: 2.5,
        "&:last-child": { pb: 2.5 },  // Override default last-child padding
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
          {/* Logo */}
          <Box 
            sx={{ 
              width: 80, 
              height: 80, 
              borderRadius: 1.5,
              border: "2px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "background.default",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {brand.logoUrl ? (
              <img 
                src={brand.logoUrl} 
                alt={brand.name} 
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : (
              <BusinessIcon sx={{ fontSize: 40, color: "text.disabled" }} />
            )}
          </Box>
          
          {/* Actions */}
          <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
            <IconButton onClick={onEdit} size="small" color="primary" title="Edit Brand">
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={onDelete} size="small" color="error" title="Delete Brand">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        
        {/* Brand Info */}
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600, fontSize: "1.15rem" }}>
            {brand.name}
          </Typography>
          <Typography 
            variant="body2" 
            color="textSecondary" 
            sx={{ 
              mb: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              height: 42,  // Fixed height for 2 lines
              lineHeight: 1.5,
            }}
          >
            {brand.description || "No description"}
          </Typography>
        </Box>
        
        {/* Location Count - Always at bottom */}
        <Box sx={{ mt: "auto", pt: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
          <Chip
            icon={<LocationIcon />}
            label={`${locationCount} Location${locationCount !== 1 ? "s" : ""}`}
            size="medium"
            color="primary"
            variant="outlined"
            sx={{ width: "100%" }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

// Location Management Component with Pagination
const LocationManagementSection = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();
  const { confirm, ConfirmDialog } = useConfirm();
  const [locations, setLocations] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [openLocationDialog, setOpenLocationDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationForm, setLocationForm] = useState({
    name: "",
    description: "",
    brandId: "",
    address: { street: "", city: "", state: "", postalCode: "", country: "" },
    contact: { phone: "", email: "", website: "" },
    isActive: true,
    timezone: "",
  });
  const [deleteOne] = useDelete();
  const [create] = useCreate();
  const [update] = useUpdate();

  useEffect(() => {
    if (record?.id) {
      loadBrands();
      loadLocations();
    } else {
      // Clear data when creating a new tenant (no ID yet)
      setBrands([]);
      setLocations([]);
      setTotal(0);
    }
  }, [record?.id, page, rowsPerPage, searchTerm, selectedBrand]);

  const loadBrands = async () => {
    if (!record?.id) {
      setBrands([]);
      return;
    }
    
    try {
      const { data } = await dataProvider.getList("brand", {
        filter: { tenantId: record.id },
        pagination: { page: 1, perPage: 100 },
        sort: { field: "name", order: "ASC" },
      });
      setBrands(data);
    } catch (error) {
      console.error("Failed to load brands:", error);
    }
  };

  const loadLocations = async () => {
    if (!record?.id) {
      setLocations([]);
      setTotal(0);
      return;
    }
    
    setLoading(true);
    try {
      const filter: any = { tenantId: record.id };
      
      if (selectedBrand !== "all") {
        filter.brandId = selectedBrand;
      }
      
      if (searchTerm) {
        filter.name = { $regex: searchTerm, $options: "i" };
      }

      const { data, total: totalCount } = await dataProvider.getList("location", {
        filter,
        pagination: { page: page + 1, perPage: rowsPerPage },
        sort: { field: "name", order: "ASC" },
      });
      
      setLocations(data);
      setTotal(totalCount);
    } catch (error) {
      console.error("Failed to load locations:", error);
      notify("Failed to load locations", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLocationDialog = (location = null) => {
    if (location) {
      setEditingLocation(location);
      setLocationForm({
        name: location.name,
        description: location.description || "",
        brandId: location.brandId || location.context?.brandId || "",
        address: location.address || { street: "", city: "", state: "", postalCode: "", country: "" },
        contact: location.contact || { phone: "", email: "", website: "" },
        isActive: location.isActive !== false,
        timezone: location.timezone || "",
      });
    } else {
      setEditingLocation(null);
      setLocationForm({
        name: "",
        description: "",
        brandId: brands.length > 0 ? brands[0].id : "",
        address: { street: "", city: "", state: "", postalCode: "", country: "" },
        contact: { phone: "", email: "", website: "" },
        isActive: true,
        timezone: "",
      });
    }
    setOpenLocationDialog(true);
  };

  const handleCloseLocationDialog = () => {
    setOpenLocationDialog(false);
    setEditingLocation(null);
  };

  const handleSaveLocation = async () => {
    if (!locationForm.brandId) {
      notify("Please select a brand", { type: "warning" });
      return;
    }

    try {
      if (editingLocation) {
        await update("location", {
          id: editingLocation.id,
          data: {
            ...locationForm,
            tenantId: record.id,  // Explicit foreign key
            brandId: locationForm.brandId,  // Explicit foreign key
            context: { tenantId: record.id, brandId: locationForm.brandId },
          },
          previousData: editingLocation,
        });
        notify("Location updated successfully", { type: "success" });
      } else {
        await create("location", {
          data: {
            ...locationForm,
            tenantId: record.id,  // Explicit foreign key
            brandId: locationForm.brandId,  // Explicit foreign key
            context: { tenantId: record.id, brandId: locationForm.brandId },
          },
        });
        notify("Location created successfully", { type: "success" });
      }
      handleCloseLocationDialog();
      loadLocations();
    } catch (error) {
      console.error("Failed to save location:", error);
      notify("Failed to save location", { type: "error" });
    }
  };

  const handleDeleteLocation = async (location) => {
    const confirmed = await confirm({
      title: 'Delete Location',
      description: `Are you sure you want to delete ${location.name}?`,
      content: (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This will permanently remove the location and all associated data.
        </Typography>
      ),
      confirmText: 'Delete',
      confirmColor: 'error',
      variant: 'warning',
    });
    
    if (!confirmed) return;
    
    try {
      await deleteOne("location", { id: location.id, previousData: location });
      notify("Location deleted successfully", { type: "success" });
      loadLocations();
    } catch (error) {
      console.error("Failed to delete location:", error);
      notify("Failed to delete location", { type: "error" });
    }
  };

  const getBrandName = (brandId) => {
    const brand = brands.find((b) => b.id === brandId);
    return brand ? brand.name : "Unknown";
  };

  if (!record) return null;

  // Show message for new tenants (not yet saved)
  if (!record.id) {
    return (
      <Box>
        <Alert severity="info">
          Please save the tenant first before adding locations.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">Locations ({total})</Typography>
        <Button
          onClick={() => handleOpenLocationDialog()}
          label="Add Location"
          variant="contained"
          startIcon={<AddIcon />}
          disabled={brands.length === 0}
        />
      </Box>

      {brands.length === 0 ? (
        <Alert severity="warning">
          Please create at least one brand before adding locations.
        </Alert>
      ) : (
        <>
          {/* Filters */}
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <MuiTextField
              placeholder="Search locations..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              size="small"
              sx={{ flex: 1, maxWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <MuiTextField
              select
              label="Filter by Brand"
              value={selectedBrand}
              onChange={(e) => {
                setSelectedBrand(e.target.value);
                setPage(0);
              }}
              size="small"
              sx={{ minWidth: 200 }}
              SelectProps={{ native: true }}
            >
              <option value="all">All Brands</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </MuiTextField>
            <IconButton onClick={loadLocations} title="Refresh">
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* Locations Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : locations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="textSecondary">No locations found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  locations.map((location) => (
                    <TableRow 
                      key={location.id} 
                      hover
                      onClick={() => navigate(`/location/${location.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{location.name}</TableCell>
                      <TableCell>{getBrandName(location.brandId || location.context?.brandId)}</TableCell>
                      <TableCell>{location.address?.city || "-"}</TableCell>
                      <TableCell>{location.address?.state || "-"}</TableCell>
                      <TableCell>
                        <Chip
                          label={location.isActive !== false ? "Active" : "Inactive"}
                          size="small"
                          color={location.isActive !== false ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          onClick={() => navigate(`/location/${location.id}`)}
                          size="small"
                          color="primary"
                          title="Open full location editor"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteLocation(location)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </TableContainer>
        </>
      )}

      {/* Location Edit Dialog */}
      <Dialog open={openLocationDialog} onClose={handleCloseLocationDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingLocation ? "Edit Location" : "Create Location"}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MuiTextField
                  label="Name"
                  value={locationForm.name}
                  onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <MuiTextField
                  select
                  label="Brand"
                  value={locationForm.brandId}
                  onChange={(e) => setLocationForm({ ...locationForm, brandId: e.target.value })}
                  fullWidth
                  required
                  SelectProps={{ native: true }}
                >
                  <option value="">Select a brand</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </MuiTextField>
              </Grid>
              <Grid item xs={12}>
                <MuiTextField
                  label="Description"
                  value={locationForm.description}
                  onChange={(e) => setLocationForm({ ...locationForm, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
              
              {/* Address Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1 }}>
                  Address
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <MuiTextField
                  label="Street"
                  value={locationForm.address.street}
                  onChange={(e) => setLocationForm({
                    ...locationForm,
                    address: { ...locationForm.address, street: e.target.value },
                  })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <MuiTextField
                  label="City"
                  value={locationForm.address.city}
                  onChange={(e) => setLocationForm({
                    ...locationForm,
                    address: { ...locationForm.address, city: e.target.value },
                  })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={3}>
                <MuiTextField
                  label="State"
                  value={locationForm.address.state}
                  onChange={(e) => setLocationForm({
                    ...locationForm,
                    address: { ...locationForm.address, state: e.target.value },
                  })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={3}>
                <MuiTextField
                  label="Postal Code"
                  value={locationForm.address.postalCode}
                  onChange={(e) => setLocationForm({
                    ...locationForm,
                    address: { ...locationForm.address, postalCode: e.target.value },
                  })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <MuiTextField
                  label="Country"
                  value={locationForm.address.country}
                  onChange={(e) => setLocationForm({
                    ...locationForm,
                    address: { ...locationForm.address, country: e.target.value },
                  })}
                  fullWidth
                />
              </Grid>
              
              {/* Contact Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1 }}>
                  Contact Information
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <MuiTextField
                  label="Phone"
                  value={locationForm.contact.phone}
                  onChange={(e) => setLocationForm({
                    ...locationForm,
                    contact: { ...locationForm.contact, phone: e.target.value },
                  })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <MuiTextField
                  label="Email"
                  value={locationForm.contact.email}
                  onChange={(e) => setLocationForm({
                    ...locationForm,
                    contact: { ...locationForm.contact, email: e.target.value },
                  })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <MuiTextField
                  label="Website"
                  value={locationForm.contact.website}
                  onChange={(e) => setLocationForm({
                    ...locationForm,
                    contact: { ...locationForm.contact, website: e.target.value },
                  })}
                  fullWidth
                />
              </Grid>
              
              {/* Additional Fields */}
              <Grid item xs={6}>
                <MuiTextField
                  label="Timezone"
                  value={locationForm.timezone}
                  onChange={(e) => setLocationForm({ ...locationForm, timezone: e.target.value })}
                  fullWidth
                  placeholder="e.g., America/New_York"
                />
              </Grid>
              <Grid item xs={6}>
                <MuiTextField
                  select
                  label="Status"
                  value={locationForm.isActive ? "active" : "inactive"}
                  onChange={(e) => setLocationForm({
                    ...locationForm,
                    isActive: e.target.value === "active",
                  })}
                  fullWidth
                  SelectProps={{ native: true }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </MuiTextField>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLocationDialog} label="Cancel" />
          <Button
            onClick={handleSaveLocation}
            label={editingLocation ? "Update" : "Create"}
            variant="contained"
            disabled={!locationForm.name || !locationForm.brandId}
          />
        </DialogActions>
      </Dialog>
      
      <ConfirmDialog />
    </Box>
  );
};

// Tenant API Keys Section
const TenantApiKeysSection = () => {
  const record = useRecordContext();
  
  if (!record?.id) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="textSecondary">
          Save the tenant first to manage API keys
        </Typography>
      </Box>
    );
  }
  
  // CRITICAL: React Admin passes MongoDB _id in record.id
  // But we need the actual UUID which is in the id field on the backend
  // The tenant record should have both _id and id fields
  // Use context.tenantId which should be the UUID
  const tenantUuid = record.context?.tenantId || record.id;
  
  console.log('🔑 TenantApiKeys: record structure:', {
    recordId: record.id,
    contextTenantId: record.context?.tenantId,
    usingUuid: tenantUuid
  });
  
  return (
    <ApiKeyManagement
      tenantId={String(tenantUuid)}
      entityType="tenant"
      entityName={record.name}
    />
  );
};

// Tenant Deploy Apps Section - Deploy to entire tenant
const TenantDeployAppsSection = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  
  const [selectedApp, setSelectedApp] = useState('');
  const [selectedVersion, setSelectedVersion] = useState(1);
  const [brands, setBrands] = useState<any[]>([]);
  const [deviceTypeCounts, setDeviceTypeCounts] = useState<Record<string, number>>({});
  const [selectedDeviceTypes, setSelectedDeviceTypes] = useState<string[]>([]);
  const [deploying, setDeploying] = useState(false);
  
  useEffect(() => {
    if (record?.id) {
      loadBrandsAndDevices();
    }
  }, [record?.id]);
  
  const loadBrandsAndDevices = async () => {
    try {
      const { data: brandData } = await dataProvider.getList('brand', {
        filter: { tenantId: record.id },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'name', order: 'ASC' }
      });
      setBrands(brandData);
      
      const { data: devices } = await dataProvider.getList('device', {
        filter: { tenantId: record.id },
        pagination: { page: 1, perPage: 100000 },
        sort: { field: 'name', order: 'ASC' }
      });
      
      const counts: Record<string, number> = {};
      devices.forEach((device: any) => {
        counts[device.deviceType] = (counts[device.deviceType] || 0) + 1;
      });
      setDeviceTypeCounts(counts);
    } catch (error) {
      console.error('Failed to load brands/devices:', error);
    }
  };
  
  const handleDeviceTypeToggle = (deviceType: string) => {
    setSelectedDeviceTypes(prev => 
      prev.includes(deviceType)
        ? prev.filter(t => t !== deviceType)
        : [...prev, deviceType]
    );
  };
  
  const handleDeploy = async () => {
    if (!selectedApp || selectedDeviceTypes.length === 0) {
      notify('Please select an application and at least one device type', { type: 'warning' });
      return;
    }
    
    setDeploying(true);
    try {
      const filter: any = { tenantId: record.id };
      if (selectedDeviceTypes.length > 0) {
        filter.deviceType = { $in: selectedDeviceTypes };
      }
      
      const { data: devices } = await dataProvider.getList('device', {
        filter,
        pagination: { page: 1, perPage: 100000 },
        sort: { field: 'name', order: 'ASC' }
      });
      
      const deploymentPromises = devices.map((device: any) =>
        dataProvider.create('application-deployment', {
          data: {
            applicationId: selectedApp,
            applicationVersion: selectedVersion,
            tenantId: record.id,
            brandId: device.brandId,
            locationId: device.locationId,
            deviceId: device.id,
            targetDeviceTypes: selectedDeviceTypes,
            status: 'pending',
            syncStatus: 'local-only',
            deployedBy: 'admin',
          }
        })
      );
      
      await Promise.all(deploymentPromises);
      
      notify(`Successfully deployed to ${devices.length} devices across ${brands.length} brands`, { type: 'success' });
      refresh();
      
      setSelectedApp('');
      setSelectedDeviceTypes([]);
      
    } catch (error: any) {
      console.error('Deployment failed:', error);
      notify(`Deployment failed: ${error.message}`, { type: 'error' });
    } finally {
      setDeploying(false);
    }
  };
  
  if (!record?.id) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="textSecondary">
          Save the tenant first to deploy applications
        </Typography>
      </Box>
    );
  }
  
  const totalSelectedDevices = selectedDeviceTypes.reduce((sum, type) => sum + (deviceTypeCounts[type] || 0), 0);
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Deploy Applications to Tenant
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
        Deploy applications to all brands, locations, and devices under this tenant
      </Typography>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <ReferenceInput source="deployApp" reference="application">
                <AutocompleteInput 
                  optionText="name" 
                  fullWidth
                  onChange={(value: any) => setSelectedApp(value)}
                />
              </ReferenceInput>
            </Grid>
            <Grid item xs={6}>
              <MuiTextField
                label="Version"
                type="number"
                defaultValue={1}
                onChange={(e: any) => setSelectedVersion(parseInt(e.target.value))}
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  Deploying to <strong>{brands.length} brand{brands.length !== 1 ? 's' : ''}</strong> under this tenant
                </Typography>
              </Alert>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Deploy to Device Types:
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(deviceTypeCounts).map(([type, count]) => (
                  <Grid item xs={6} md={4} key={type}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedDeviceTypes.includes(type)}
                          onChange={() => handleDeviceTypeToggle(type)}
                        />
                      }
                      label={`${type.toUpperCase()} (${count} device${count !== 1 ? 's' : ''})`}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
            
            {selectedDeviceTypes.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="success">
                  Will deploy to <strong>{totalSelectedDevices} device{totalSelectedDevices !== 1 ? 's' : ''}</strong> across all tenant brands
                </Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <MuiButton
                variant="contained"
                color="primary"
                onClick={handleDeploy}
                disabled={!selectedApp || selectedDeviceTypes.length === 0 || deploying}
                startIcon={<AddIcon />}
              >
                {deploying ? 'Deploying...' : `Deploy to Tenant (${totalSelectedDevices} devices)`}
              </MuiButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Tenant-Wide Deployments
      </Typography>
      
      <ReferenceManyField
        label=""
        reference="application-deployment"
        target="tenantId"
        filter={{ brandId: null, locationId: null, deviceId: null }}
        sort={{ field: 'deployedAt', order: 'DESC' }}
      >
        <Datagrid bulkActionButtons={false}>
          <FunctionField
            label="Application"
            render={(record: any) => (
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {record.applicationKey || record.applicationId}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Version {record.applicationVersion}
                </Typography>
              </Box>
            )}
          />
          <FunctionField
            label="Device Types"
            render={(record: any) => {
              if (record.targetDeviceTypes?.length > 0) {
                return record.targetDeviceTypes.join(', ');
              }
              return 'All Types';
            }}
          />
          <ChipField source="status" />
          <DateField source="deployedAt" showTime />
          <TextField source="deployedBy" />
        </Datagrid>
      </ReferenceManyField>
    </Box>
  );
};

// Main Tenant Edit Component
export const TenantEditEnhanced = () => {
  return (
    <Edit>
      <TabbedForm>
        <FormTab label="General">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardHeader title="Tenant Information" />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextInput source="name" label="Name" fullWidth required />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SelectInput
                        source="type"
                        label="Type"
                        choices={[
                          { id: "RESTAURANT", name: "Restaurant" },
                          { id: "RETAIL", name: "Retail" },
                        ]}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextInput
                        source="description"
                        label="Description"
                        fullWidth
                        multiline
                        rows={3}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TenantLogoUpload />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Summary Statistics */}
            <Grid item xs={12}>
              <TenantSummaryStats />
            </Grid>
          </Grid>
        </FormTab>

        <FormTab label="Brands">
          <BrandManagementSection />
        </FormTab>

        <FormTab label="Locations">
          <LocationManagementSection />
        </FormTab>
        
        <FormTab label="Deploy Apps">
          <TenantDeployAppsSection />
        </FormTab>
        
        <FormTab label="API Keys">
          <TenantApiKeysSection />
        </FormTab>
        
        <FormTab label="Sync from Parent">
          <TenantConfigSync />
        </FormTab>
      </TabbedForm>
    </Edit>
  );
};

// Tenant Logo Upload Component
const TenantLogoUpload = () => {
  const {
    field,
    fieldState: { error },
  } = useInput({ source: "logoUrl" });

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        field.onChange(base64);  // Proper React Admin form integration
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    field.onChange("");  // Proper React Admin form integration
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Tenant Logo
      </Typography>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: 1,
            border: "2px dashed",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default",
            overflow: "hidden",
          }}
        >
          {field.value ? (
            <img
              src={field.value}
              alt="Tenant logo preview"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          ) : (
            <BusinessIcon sx={{ fontSize: 48, color: "text.disabled" }} />
          )}
        </Box>
        <Box>
          <input
            accept="image/*"
            style={{ display: "none" }}
            id="tenant-logo-upload"
            type="file"
            onChange={handleLogoChange}
          />
          <label htmlFor="tenant-logo-upload">
            <MuiButton variant="outlined" component="span">
              Upload Logo
            </MuiButton>
          </label>
          {field.value && (
            <MuiButton
              variant="text"
              color="error"
              onClick={handleRemoveLogo}
              sx={{ ml: 1 }}
            >
              Remove
            </MuiButton>
          )}
          <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
            Recommended: Square image, at least 200x200px
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Summary Stats Component
const TenantSummaryStats = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const [stats, setStats] = useState({ brands: 0, locations: 0, activeLocations: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (record?.id) {
      loadStats();
    } else {
      // Reset stats for new tenant
      setStats({ brands: 0, locations: 0, activeLocations: 0 });
    }
  }, [record?.id]);

  const loadStats = async () => {
    if (!record?.id) {
      setStats({ brands: 0, locations: 0, activeLocations: 0 });
      return;
    }
    
    setLoading(true);
    try {
      const [brandsResult, locationsResult, activeLocationsResult] = await Promise.all([
        dataProvider.getList("brand", {
          filter: { tenantId: record.id },
          pagination: { page: 1, perPage: 1 },
          sort: { field: "name", order: "ASC" },
        }),
        dataProvider.getList("location", {
          filter: { tenantId: record.id },
          pagination: { page: 1, perPage: 1 },
          sort: { field: "name", order: "ASC" },
        }),
        dataProvider.getList("location", {
          filter: { tenantId: record.id, isActive: true },
          pagination: { page: 1, perPage: 1 },
          sort: { field: "name", order: "ASC" },
        }),
      ]);

      setStats({
        brands: brandsResult.total,
        locations: locationsResult.total,
        activeLocations: activeLocationsResult.total,
      });
    } catch (error) {
      console.error("Failed to load stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent sx={{ textAlign: "center" }}>
          <CircularProgress size={24} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: "flex", gap: 3, alignItems: "center", mb: 3 }}>
          {/* Tenant Logo */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: 2,
              border: "2px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "background.default",
              overflow: "hidden",
            }}
          >
            {record?.logoUrl ? (
              <img
                src={record.logoUrl}
                alt={record.name}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : (
              <BusinessIcon sx={{ fontSize: 40, color: "text.disabled" }} />
            )}
          </Box>
          
          {/* Tenant Name */}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {record?.name || "Tenant"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {record?.type === "RESTAURANT" ? "Restaurant" : record?.type === "RETAIL" ? "Retail" : "Tenant"} Summary
            </Typography>
          </Box>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center", p: 2, borderRadius: 1, bgcolor: "background.default" }}>
              <BusinessIcon sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
              <Typography variant="h4">{stats.brands}</Typography>
              <Typography variant="body2" color="textSecondary">
                Brand{stats.brands !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center", p: 2, borderRadius: 1, bgcolor: "background.default" }}>
              <LocationIcon sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
              <Typography variant="h4">{stats.locations}</Typography>
              <Typography variant="body2" color="textSecondary">
                Total Location{stats.locations !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center", p: 2, borderRadius: 1, bgcolor: "background.default" }}>
              <LocationIcon sx={{ fontSize: 40, color: "success.main", mb: 1 }} />
              <Typography variant="h4">{stats.activeLocations}</Typography>
              <Typography variant="body2" color="textSecondary">
                Active Location{stats.activeLocations !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default TenantEditEnhanced;

